import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AuthContext from '../../context/authContext';
import SocketIOContext from '../../context/socketIOContext';
import useEmployeeChat from '../../hooks/useEmployeeChat';
import ConversationList from '../employee-chat/ConversationList';
import EmployeeDirectory from '../employee-chat/EmployeeDirectory';
import ChatWindow from '../employee-chat/ChatWindow';

// Merge a message into a list (replace by id, else append), keeping ascending order.
const upsertMessage = (list, message) => {
  const idx = list.findIndex(m => m.id === message.id);
  let next;
  if (idx === -1) next = [...list, message];
  else {
    next = [...list];
    next[idx] = message;
  }
  return next.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
};

export default function MitarbeiterChat() {
  const authCtx = useContext(AuthContext);
  const socketCtx = useContext(SocketIOContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    getDirectory,
    getConversations,
    getOrCreateConversation,
    getConversation,
    getMessages,
    sendMessage,
    editMessage,
    deleteMessage,
    toggleReaction,
    markRead,
    updateConversationState,
    uploadAttachment,
    getPermissions,
  } = useEmployeeChat();

  const currentUserId = authCtx?.currentUser?.userId || '';
  // Moderation is device-independent: the backend resolves the right across
  // all device scopes of the user's roles. The device-scoped JWT right only
  // serves as the initial value until the permissions request answers.
  const [canModerate, setCanModerate] = useState(() =>
    authCtx?.checkRight ? authCtx.checkRight('employeeChat:manage') : false
  );

  const [tab, setTab] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pageSeq, setPageSeq] = useState(0);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingActive, setTypingActive] = useState(false);
  const [startingId, setStartingId] = useState(null);

  const activeId = activeConversation?.id || null;

  // Refs for socket handlers (avoid re-subscribing on every state change).
  const activeIdRef = useRef(activeId);
  const currentUserIdRef = useRef(currentUserId);
  const markReadRef = useRef(markRead);
  const typingTimerRef = useRef(null);
  const refreshConversationsRef = useRef(() => {});
  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);
  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);
  useEffect(() => {
    markReadRef.current = markRead;
  }, [markRead]);

  const refreshConversations = useCallback(async () => {
    try {
      const res = await getConversations();
      const list = res?.conversations || [];
      setConversations(list);
      setOnlineUsers(prev => {
        const next = new Set(prev);
        list.forEach(c => {
          if (c.otherUser?.isOnline) next.add(c.otherUser.id);
        });
        return next;
      });
    } catch (_e) {
      // handled in hook
    }
  }, [getConversations]);

  useEffect(() => {
    refreshConversationsRef.current = refreshConversations;
  }, [refreshConversations]);

  // Initial load.
  useEffect(() => {
    refreshConversations();
  }, [refreshConversations]);

  // Device-independent moderation capability from the backend.
  useEffect(() => {
    let cancelled = false;
    const loadPermissions = async () => {
      try {
        const res = await getPermissions();
        if (!cancelled) setCanModerate(!!res?.canModerate);
      } catch {
        // Keep the JWT-based initial value when the request fails.
      }
    };
    loadPermissions();
    return () => {
      cancelled = true;
    };
  }, [getPermissions]);

  // Socket subscriptions.
  useEffect(() => {
    if (!socketCtx || typeof socketCtx.on !== 'function') return undefined;

    const offMessage = socketCtx.on('chat:message', payload => {
      if (!payload || !payload.conversationId) return;
      const { conversationId, message, lastMessage, lastMessageAt } = payload;
      const isActive = activeIdRef.current === conversationId;
      const fromMe = String(message.senderId) === String(currentUserIdRef.current);

      setConversations(prev => {
        const idx = prev.findIndex(c => c.id === conversationId);
        if (idx === -1) {
          refreshConversationsRef.current();
          return prev;
        }
        const copy = [...prev];
        const c = { ...copy[idx], lastMessage, lastMessageAt };
        if (!isActive && !fromMe) c.unreadCount = (c.unreadCount || 0) + 1;
        copy[idx] = c;
        return copy;
      });

      if (isActive) {
        setMessages(prev => upsertMessage(prev, message));
        if (!fromMe) markReadRef.current(conversationId, message.id).catch(() => {});
      }
    });

    const offUpdated = socketCtx.on('chat:message:updated', payload => {
      if (!payload || activeIdRef.current !== payload.conversationId) return;
      setMessages(prev => upsertMessage(prev, payload.message));
    });

    const offRead = socketCtx.on('chat:read', payload => {
      if (!payload || activeIdRef.current !== payload.conversationId) return;
      if (String(payload.userId) === String(currentUserIdRef.current)) return;
      setActiveConversation(prev => (prev ? { ...prev, otherLastReadAt: payload.lastReadAt } : prev));
    });

    const offTyping = socketCtx.on('chat:typing', payload => {
      if (!payload || activeIdRef.current !== payload.conversationId) return;
      if (String(payload.userId) === String(currentUserIdRef.current)) return;
      setTypingActive(!!payload.isTyping);
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      if (payload.isTyping) {
        typingTimerRef.current = setTimeout(() => setTypingActive(false), 4000);
      }
    });

    const offPresence = socketCtx.on('chat:presence', payload => {
      if (!payload || !payload.userId) return;
      setOnlineUsers(prev => {
        const next = new Set(prev);
        if (payload.online) next.add(String(payload.userId));
        else next.delete(String(payload.userId));
        return next;
      });
    });

    return () => {
      offMessage && offMessage();
      offUpdated && offUpdated();
      offRead && offRead();
      offTyping && offTyping();
      offPresence && offPresence();
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, [socketCtx]);

  const openConversation = useCallback(
    async conv => {
      setActiveConversation(conv);
      setMessages([]);
      setTypingActive(false);
      setTab(0);
      try {
        const res = await getConversation(conv.id);
        setActiveConversation(res.conversation);
        setMessages(res.messages || []);
        setPageSeq(res.page?.seq ?? 0);
        setHasMoreOlder(!!res.page?.hasMore);
        setConversations(prev =>
          prev.map(c => (c.id === conv.id ? { ...c, unreadCount: 0 } : c))
        );
        markReadRef.current(conv.id).catch(() => {});
      } catch (_e) {
        // handled in hook
      }
    },
    [getConversation]
  );

  // Deep link (?conversation=<id>): opens the referenced conversation once the
  // inbox list is loaded (used by the dashboard widget), then cleans the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    if (deepLinkHandledRef.current) return;
    const targetId = searchParams.get('conversation');
    if (!targetId) {
      deepLinkHandledRef.current = true;
      return;
    }
    if (conversations.length === 0) return;
    deepLinkHandledRef.current = true;
    const conv = conversations.find(c => c.id === targetId);
    if (conv) openConversation(conv);
    setSearchParams({}, { replace: true });
  }, [conversations, searchParams, openConversation, setSearchParams]);

  const loadOlder = useCallback(async () => {
    if (!activeId || !hasMoreOlder || isLoadingOlder) return;
    setIsLoadingOlder(true);
    try {
      const res = await getMessages(activeId, pageSeq);
      const older = res.messages || [];
      setMessages(prev => {
        const seen = new Set(prev.map(m => m.id));
        const merged = [...older.filter(m => !seen.has(m.id)), ...prev];
        return merged.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      });
      setPageSeq(res.seq);
      setHasMoreOlder(!!res.hasMore);
    } catch (_e) {
      // handled in hook
    } finally {
      setIsLoadingOlder(false);
    }
  }, [activeId, hasMoreOlder, isLoadingOlder, pageSeq, getMessages]);

  const startChat = useCallback(
    async emp => {
      setStartingId(emp.id);
      try {
        const res = await getOrCreateConversation(emp.id);
        const conv = res.conversation;
        setConversations(prev => {
          if (prev.some(c => c.id === conv.id)) return prev;
          return [conv, ...prev];
        });
        await openConversation(conv);
      } catch (_e) {
        // handled in hook
      } finally {
        setStartingId(null);
      }
    },
    [getOrCreateConversation, openConversation]
  );

  const handleSend = useCallback(
    async payload => {
      if (!activeId) return;
      try {
        const res = await sendMessage(activeId, payload);
        if (res?.message) {
          setMessages(prev => upsertMessage(prev, res.message));
          setConversations(prev =>
            prev.map(c =>
              c.id === activeId
                ? {
                    ...c,
                    lastMessage: {
                      senderId: currentUserId,
                      type: res.message.type,
                      text: res.message.text || (res.message.attachments?.length ? 'Anhang' : ''),
                      sentAt: res.message.createdAt,
                    },
                    lastMessageAt: res.message.createdAt,
                  }
                : c
            )
          );
        }
      } catch (_e) {
        // handled in hook
      }
    },
    [activeId, sendMessage, currentUserId]
  );

  const handleEdit = useCallback(
    async (messageId, text) => {
      const res = await editMessage(activeId, messageId, text);
      if (res?.message) setMessages(prev => upsertMessage(prev, res.message));
    },
    [activeId, editMessage]
  );

  const handleDelete = useCallback(
    async messageId => {
      const res = await deleteMessage(activeId, messageId);
      if (res?.message) setMessages(prev => upsertMessage(prev, res.message));
    },
    [activeId, deleteMessage]
  );

  const handleReact = useCallback(
    async (messageId, emoji) => {
      const res = await toggleReaction(activeId, messageId, emoji);
      if (res?.message) setMessages(prev => upsertMessage(prev, res.message));
    },
    [activeId, toggleReaction]
  );

  const handleUpdateState = useCallback(
    async patch => {
      const res = await updateConversationState(activeId, patch);
      if (res?.conversation) {
        setActiveConversation(res.conversation);
        setConversations(prev => prev.map(c => (c.id === activeId ? res.conversation : c)));
        if (patch.cleared) setMessages([]);
      }
    },
    [activeId, updateConversationState]
  );

  const handleUpload = useCallback(
    async payload => {
      if (!activeId) return null;
      return uploadAttachment(activeId, payload);
    },
    [activeId, uploadAttachment]
  );

  const handleTyping = useCallback(
    isTyping => {
      if (activeId && socketCtx?.emit) socketCtx.emit('chat:typing', { conversationId: activeId, isTyping });
    },
    [activeId, socketCtx]
  );

  const totalUnread = useMemo(
    () => conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0),
    [conversations]
  );

  const appBarHeight = isMobile ? 56 : 64;
  const verticalPadding = 48;
  const containerHeight = `calc(100vh - ${appBarHeight}px - ${verticalPadding}px)`;

  const showListPane = !isMobile || !activeId;
  const showChatPane = !isMobile || !!activeId;

  const leftPane = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant='fullWidth'>
        <Tab label={totalUnread > 0 ? `Chats (${totalUnread})` : 'Chats'} />
        <Tab label='Kolleg:innen' />
      </Tabs>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {tab === 0 ? (
          <ConversationList
            conversations={conversations}
            activeConversationId={activeId}
            currentUserId={currentUserId}
            onlineUsers={onlineUsers}
            onSelect={openConversation}
          />
        ) : (
          <EmployeeDirectory
            getDirectory={getDirectory}
            onlineUsers={onlineUsers}
            onStartChat={startChat}
            startingId={startingId}
          />
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ height: containerHeight, px: { xs: 0, sm: 1 } }}>
      <Paper
        variant='outlined'
        sx={{ height: '100%', display: 'flex', overflow: 'hidden', borderRadius: 2 }}
      >
        {showListPane && (
          <Box
            sx={{
              width: isMobile ? '100%' : 340,
              flexShrink: 0,
              borderRight: theme => (isMobile ? 'none' : `1px solid ${theme.palette.divider}`),
              minHeight: 0,
            }}
          >
            {leftPane}
          </Box>
        )}

        {showChatPane && (
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            {activeConversation ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                {isMobile && (
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pt: 1 }}>
                    <IconButton onClick={() => setActiveConversation(null)} size='small'>
                      <ArrowBackIcon />
                    </IconButton>
                    <Typography variant='body2'>Zurück</Typography>
                  </Box>
                )}
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <ChatWindow
                    conversation={activeConversation}
                    messages={messages}
                    hasMoreOlder={hasMoreOlder}
                    isLoadingOlder={isLoadingOlder}
                    onLoadOlder={loadOlder}
                    currentUserId={currentUserId}
                    typing={typingActive}
                    onSend={handleSend}
                    onEditMessage={handleEdit}
                    onDeleteMessage={handleDelete}
                    onReact={handleReact}
                    onUpdateState={handleUpdateState}
                    onUpload={handleUpload}
                    onTyping={handleTyping}
                    canModerate={canModerate}
                  />
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  gap: 1,
                  px: 3,
                  textAlign: 'center',
                }}
              >
                <ChatBubbleOutlineIcon sx={{ fontSize: 48, opacity: 0.4 }} />
                <Typography variant='h6'>Mitarbeiter-Chat</Typography>
                <Typography variant='body2'>
                  Wähle eine Unterhaltung aus oder starte über „Kolleg:innen“ eine neue.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
